import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { Loader, EmptyState } from "@/components/common";
import ProductCard from "@/components/ProductCard";
import useProductRatings from "@/hooks/useProductRatings";
import { hasPromo, effectivePrice } from "@/lib/format";

export default function Catalog() {
  const [params, setParams] = useSearchParams();
  const ratings = useProductRatings();
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoOnly, setPromoOnly] = useState(params.get("promo") === "1");
  const [packsOnly, setPacksOnly] = useState(false);
  const [topRated, setTopRated] = useState(false);
  const [newOnly, setNewOnly] = useState(false);
  const [sort, setSort] = useState("default");
  const q = params.get("q") || "";
  const category = params.get("category") || "";

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", { params: { q: q || undefined, category: category || undefined } });
      setProducts(data);
    } finally { setLoading(false); }
  }, [q, category]);

  useEffect(() => { load(); }, [load]);

  const setCat = (id) => {
    const next = new URLSearchParams(params);
    if (id) next.set("category", id); else next.delete("category");
    setParams(next);
  };
  const onSearch = (e) => {
    const next = new URLSearchParams(params);
    if (e.target.value) next.set("q", e.target.value); else next.delete("q");
    setParams(next);
  };

  const NEW_MS = 30 * 24 * 3600 * 1000;
  const isNew = (p) => p.created_at && (Date.now() - new Date(p.created_at).getTime()) < NEW_MS;
  const filtered = (promoOnly ? products.filter(hasPromo) : products)
    .filter((p) => !packsOnly || p.is_pack)
    .filter((p) => !topRated || (ratings[p.id]?.average || 0) >= 4)
    .filter((p) => !newOnly || isNew(p));
  let shown = filtered;
  if (sort === "rating") shown = [...filtered].sort((a, b) => ((ratings[b.id]?.average || 0) - (ratings[a.id]?.average || 0)) || ((ratings[b.id]?.count || 0) - (ratings[a.id]?.count || 0)));
  else if (sort === "price_asc") shown = [...filtered].sort((a, b) => effectivePrice(a) - effectivePrice(b));
  else if (sort === "price_desc") shown = [...filtered].sort((a, b) => effectivePrice(b) - effectivePrice(a));
  else if (sort === "newest") shown = [...filtered].sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  return (
    <div className="space-y-5">
      <h1 className="font-display text-3xl font-extrabold text-[#0F172A]">Catalogue</h1>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input defaultValue={q} onChange={onSearch} placeholder="Rechercher un matériau, une marque..."
          className="w-full bg-white border border-slate-300 rounded-full pl-9 pr-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#E08B00]/40" data-testid="catalog-search" />
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 flex-1">
          <Chip active={!category} onClick={() => setCat("")} testid="chip-all">Tout</Chip>
          <Chip active={promoOnly} onClick={() => setPromoOnly((v) => !v)} testid="chip-promo">🔖 En promotion</Chip>
          <Chip active={packsOnly} onClick={() => setPacksOnly((v) => !v)} testid="chip-packs">📦 Packs & pose</Chip>
          <Chip active={topRated} onClick={() => setTopRated((v) => !v)} testid="chip-toprated">⭐ 4★ et plus</Chip>
          <Chip active={newOnly} onClick={() => setNewOnly((v) => !v)} testid="chip-nouveautes">✨ Nouveautés</Chip>
          {categories.map((c) => (
            <Chip key={c.id} active={category === c.id} onClick={() => setCat(c.id)} testid={`chip-${c.slug}`}>{c.name}</Chip>
          ))}
        </div>
        <select value={sort} onChange={(e) => setSort(e.target.value)} className="shrink-0 bg-white border border-slate-300 rounded-full px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-[#E08B00]/40" data-testid="catalog-sort">
          <option value="default">Trier : par défaut</option>
          <option value="newest">✨ Nouveautés d'abord</option>
          <option value="rating">⭐ Mieux notés</option>
          <option value="price_asc">Prix croissant</option>
          <option value="price_desc">Prix décroissant</option>
        </select>
      </div>

      {loading ? <Loader /> : shown.length === 0 ? (
        <EmptyState icon={Search} title="Aucun produit trouvé" subtitle={promoOnly ? "Aucun article en promotion pour cette sélection." : "Essayez un autre mot-clé ou catégorie."} />
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {shown.map((p) => <ProductCard key={p.id} p={p} rating={ratings[p.id]} />)}
        </div>
      )}
    </div>
  );
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-semibold transition ${active ? "bg-[#C85A32] text-white" : "bg-white border border-slate-300 text-slate-600 hover:border-[#C85A32]/50"}`}>
      {children}
    </button>
  );
}
